/**
 * The queue/flush state machine for a leader handover. PURE — no browser APIs,
 * no timers of its own, an injected `now()` — which is the entire reason it is a
 * separate module: multi-tab behaviour is untestable in Jest (no
 * `BroadcastChannel`, no `navigator.locks`, no `Worker`), but THIS is testable,
 * and it holds every decision that can silently corrupt data.
 *
 * The safety argument lives in the two-list split, so it is explicit in the code:
 *
 *   - `queued`   — never dispatched. Safe to hold and flush against a NEW leader,
 *                  because nothing was ever sent, so at-least-once cannot apply.
 *   - `inFlight` — dispatched and unanswered. INDETERMINATE across a leader
 *                  change: the old leader may have applied, partially applied, or
 *                  never seen them. Rejected with `LeaderChangedError`, never
 *                  re-sent (settled decision D1: at-most-once for dispatched
 *                  requests, so a non-idempotent write can never double-apply).
 *
 * Ordering is FIFO and GLOBAL across methods, with no read/write distinction.
 * That is load-bearing, not laziness: drizzle implements transactions by issuing
 * `begin` … statements … `commit` as separate calls, so any reordering — including
 * "optimize" by dispatching a new call directly while a flush is running — would
 * break a transaction apart. New calls arriving during a flush append to the TAIL.
 */
import { boundedPromise, unboundedWait } from "@-/dev.improve.haniverse.lib.deadline";
import { LeaderChangedError, LeaderUnavailableError } from "#leader-resource-errors";

/**
 * Hard cap on retained requests. Unbounded is not acceptable for robustness: each
 * entry retains its `args` (for a SQL call, `[sql, params]`) for the whole
 * handover. 5_000 is far above any plausible legitimate burst while still
 * bounding worst-case memory.
 */
export const MAX_QUEUED_REQUESTS = 5000;

/**
 * Dispatch at most this many requests per synchronous chunk, then yield.
 *
 * NOT batching for throughput (settled decision D2 says volume is not a
 * constraint). It is yielding so the RESPONSE path can make progress: flushing
 * thousands of `postMessage`s in one synchronous loop starves the event loop and
 * delays the very `rpc-return`s the flush is waiting on.
 */
export const FLUSH_CHUNK_SIZE = 64;

export const DEFAULT_HANDOVER_BUDGET_MS = 20_000;
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Upper bound on any single request's dispatch budget, whatever a leader advertises for its
 * method. The largest legitimate one (a sync push of a replayed guest database) is a few minutes.
 */
export const MAX_REQUEST_TIMEOUT_MS = 5 * 60_000;

/**
 * A 0ms yield between flush chunks settles on the next macrotask. If the injected timer ever
 * failed to fire, the flush would stall with the queue marked `flushing`, so the yield is a hold
 * that fails OPEN after this long: the flush simply continues.
 */
const FLUSH_YIELD_DEADLINE_MS = 1000;

export interface LeaderQueueEntry {
  args: readonly unknown[];
  callId: string;
  method: string;
}

/** What the queue asks its owner to do. The owner owns all I/O; the queue owns ordering. */
export interface LeaderRequestQueueOptions {
  /** Send one request to the current leader. Called only while connected. */
  dispatch(entry: LeaderQueueEntry): void;
  /**
   * Max time a queued request may wait during a handover, measured from when
   * handover STARTED — not per request.
   */
  handoverBudgetMs?: number;
  /**
   * Is a DISPATCHED request safe to re-send to a NEW leader after the old one
   * died mid-flight?
   *
   * INJECTED, not computed here, for the same reason `now`/`setTimer` are: this
   * module is deliberately free of SQL knowledge (and of browser APIs), which is
   * what keeps it unit-testable. The SQL-aware policy lives with the consumer
   * that already owns the statement vocabulary.
   *
   * Called ONCE PER REQUEST AT DISPATCH — never during handover. That ordering
   * is load-bearing: a stream-stateful classifier (transaction depth is derived
   * from the `begin`/`commit` statements as they go by) must observe requests in
   * dispatch order exactly once, and `enterHandover` walks `inFlight` in
   * arbitrary Map order at an arbitrary later time.
   *
   * Defaults to "nothing is re-sendable", i.e. the pre-existing at-most-once
   * behaviour for every request. A consumer opts INTO re-sending reads.
   */
  isResendable?(entry: LeaderQueueEntry): boolean;
  now(): number;
  /**
   * Yield to a macrotask between flush chunks. Injected so tests can observe the
   * chunk boundary without fake timers.
   */
  onFlushYield?(): Promise<void>;
  /** Notified whenever the queued count or handover start changes, for status UI. */
  onQueueChange?(queuedCount: number): void;
  /**
   * Per-request budget for a DISPATCHED request. The clock starts at DISPATCH,
   * not at enqueue — a request that waited 15s in the queue still gets its full
   * budget. The deleted broker used one wall-clock budget for both and paid for
   * it on CPU-starved CI hosts.
   */
  requestTimeoutMs?: number;
  /**
   * Per-METHOD dispatch budget, overriding {@link requestTimeoutMs} (clamped to
   * {@link MAX_REQUEST_TIMEOUT_MS}). Read at DISPATCH, so a budget learned from
   * the leader after a request was queued still applies to it.
   *
   * One budget for every method was wrong in both directions: a sync push of a
   * replayed guest database or an engine reopen (a ~36s connect ladder) outlives
   * any budget short enough for a read, and a tab that gives up BEFORE the leader
   * has answered reports a failure for a write that may still land.
   */
  requestTimeoutMsFor?(method: string): number | undefined;
  /**
   * Schedule `fn` after `ms`, returning a cancel. Injected so the queue stays
   * free of `setTimeout` and remains testable without fake timers.
   */
  setTimer(fn: () => void, ms: number): () => void;
}

export interface LeaderRequestQueue {
  /**
   * Leave handover: a leader is connected. Flushes `queued` in enqueue order, in
   * chunks. Resolves when the flush finishes.
   */
  connect(): Promise<void>;
  /** True once a leader is connected and requests dispatch immediately. */
  readonly connected: boolean;
  /** Reject everything (queued and in-flight) — used on client teardown. */
  destroy(reason: string): void;
  /**
   * Enter handover. Rejects `inFlight` with `LeaderChangedError` (indeterminate),
   * RETAINS `queued`, and starts the budget clock if it is not already running.
   */
  enterHandover(): void;
  /**
   * Enter a PAUSE: the same leader has temporarily closed the resource (the Turso
   * sign-in wipe). New calls queue and the budget clock runs, exactly as in a
   * handover.
   *
   * The difference from {@link enterHandover} is what it does NOT do: it does not
   * touch `inFlight`. Leadership did not change, so those calls still belong to
   * the current leader, which rejects them itself before closing and answers with
   * ordinary `rpc-error`s. Rejecting them here as well would settle each promise
   * twice — harmless for the caller (the first settle wins) but it would
   * misattribute a deliberate close as a leader change, and `LeaderChangedError`
   * is precisely the signal the lost-write marker counts.
   */
  enterPause(): void;
  /** Reject a dispatched request with a resource-level error from the leader. */
  failInFlight(callId: string, error: Error): void;
  readonly inFlightCount: number;
  readonly queuedCount: number;
  /** Enqueue a call, returning the promise the caller awaits. */
  request(
    method: string,
    args: readonly unknown[],
    callId: string,
    options?: {
      /**
       * Dispatch while deliberately paused. Reserved for the host-declared
       * resume method — without this bypass, the call that emits
       * `leader-resumed` would itself wait forever for `leader-resumed`.
       */
      resumesPausedLeader?: boolean;
    },
  ): Promise<unknown>;
  /** Resolve a dispatched request with the leader's result. */
  resolveInFlight(callId: string, result: unknown): void;
  /** Elapsed ms since handover started, or `null` when connected. */
  readonly sinceHandoverMs: number | null;
}

interface PendingEntry extends LeaderQueueEntry {
  /** Cancels the lifetime backstop; called once the entry settles by any route. */
  cancelLifetime: (() => void) | null;
  cancelTimeout: (() => void) | null;
  reject(error: Error): void;
  /**
   * Whether this request may be re-sent to a new leader, decided AT DISPATCH by
   * `options.isResendable` and frozen here. Meaningless until dispatched.
   */
  resendable: boolean;
  resolve(value: unknown): void;
}

export function createLeaderRequestQueue(options: LeaderRequestQueueOptions): LeaderRequestQueue {
  const handoverBudgetMs = options.handoverBudgetMs ?? DEFAULT_HANDOVER_BUDGET_MS;
  const requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  /**
   * Backstop over a request's WHOLE life. Each phase is already bounded (the
   * handover budget while queued, the dispatch budget while in flight), but a
   * resendable read is re-queued on every leader change, so a flapping election
   * could otherwise cycle it between the two forever. Two full cycles at the
   * largest possible budget is far beyond anything legitimate.
   */
  const requestLifetimeMs = 2 * (handoverBudgetMs + MAX_REQUEST_TIMEOUT_MS);

  function timeoutFor(method: string): number {
    const perMethod = options.requestTimeoutMsFor?.(method);
    return perMethod === undefined
      ? requestTimeoutMs
      : Math.min(Math.max(perMethod, 1), MAX_REQUEST_TIMEOUT_MS);
  }

  const queued: PendingEntry[] = [];
  const inFlight = new Map<string, PendingEntry>();

  let connected = false;
  // True for the duration of a flush. While set, NEW calls must append to the
  // tail rather than dispatch directly — dispatching directly would let a call
  // issued mid-flush overtake the queued remainder, which for a queued
  // transaction means a statement landing before its own `begin`.
  let flushing = false;
  // Null while connected; the `now()` at which the current handover began otherwise.
  // Starts non-null: before the first `connect()` the client IS in handover
  // (§2.8 "entering handover" case (c)), and the budget must already be running.
  let handoverStartedAt: number | null = options.now();
  let cancelBudgetTimer: (() => void) | null = null;
  let destroyed = false;
  let paused = false;

  function notifyQueueChange(): void {
    options.onQueueChange?.(queued.length);
  }

  function startBudgetTimer(): void {
    if (cancelBudgetTimer !== null) {
      return;
    }
    cancelBudgetTimer = options.setTimer(() => {
      cancelBudgetTimer = null;
      if (connected || destroyed) {
        return;
      }
      const elapsed =
        handoverStartedAt === null ? handoverBudgetMs : options.now() - handoverStartedAt;
      // The announce loop keeps running in the client, so a leader that appears
      // LATER still recovers this tab for SUBSEQUENT calls. Only the currently
      // queued requests fail.
      rejectAllQueued(
        new LeaderUnavailableError(
          `No database leader available after ${elapsed}ms — every tab failed to open the resource.`,
        ),
      );
    }, handoverBudgetMs);
  }

  function stopBudgetTimer(): void {
    cancelBudgetTimer?.();
    cancelBudgetTimer = null;
  }

  function rejectAllQueued(error: Error): void {
    const entries = queued.splice(0);
    for (const entry of entries) {
      entry.reject(error);
    }
    if (entries.length > 0) {
      notifyQueueChange();
    }
  }

  function dispatchEntry(entry: PendingEntry): void {
    // Classify HERE, once, in dispatch order — see `isResendable`'s contract for
    // why a stream-stateful classifier cannot be called from `enterHandover`.
    entry.resendable = options.isResendable?.(entry) ?? false;
    inFlight.set(entry.callId, entry);
    // Per-request clock starts HERE, at dispatch — never at enqueue.
    const timeoutMs = timeoutFor(entry.method);
    entry.cancelTimeout = options.setTimer(() => {
      entry.cancelTimeout = null;
      if (!inFlight.delete(entry.callId)) {
        return;
      }
      entry.reject(
        new LeaderUnavailableError(
          `Leader request "${entry.method}" timed out after ${timeoutMs}ms`,
        ),
      );
    }, timeoutMs);
    options.dispatch(entry);
  }

  /**
   * Remove and return every dispatched request. Drained into an array FIRST
   * because the callers reject each entry, and rejecting mutates `inFlight` —
   * iterating the live Map while mutating it would skip entries.
   */
  function drainInFlight(): PendingEntry[] {
    const entries = [...inFlight.values()];
    for (const entry of entries) {
      entry.cancelTimeout?.();
      entry.cancelTimeout = null;
    }
    inFlight.clear();
    return entries;
  }

  function settleInFlight(callId: string): PendingEntry | undefined {
    const entry = inFlight.get(callId);
    if (entry === undefined) {
      return undefined;
    }
    inFlight.delete(callId);
    entry.cancelTimeout?.();
    entry.cancelTimeout = null;
    return entry;
  }

  async function flush(): Promise<void> {
    flushing = true;
    try {
      await flushChunks();
    } finally {
      flushing = false;
    }
  }

  async function flushChunks(): Promise<void> {
    while (connected && queued.length > 0) {
      // Take at most one chunk, dispatch it synchronously (preserving order),
      // then yield so responses can land before the next chunk.
      const chunk = queued.splice(0, FLUSH_CHUNK_SIZE);
      for (const entry of chunk) {
        dispatchEntry(entry);
      }
      notifyQueueChange();
      if (queued.length > 0) {
        await (options.onFlushYield?.() ??
          boundedPromise(
            {
              deadline: FLUSH_YIELD_DEADLINE_MS,
              onDeadline: "resolve",
              operation: "leader request flush yield",
            },
            (resolve) => options.setTimer(resolve, 0),
          ));
      }
    }
  }

  return {
    connect: async () => {
      if (destroyed) {
        return;
      }
      connected = true;
      paused = false;
      handoverStartedAt = null;
      stopBudgetTimer();
      await flush();
    },
    get connected() {
      return connected;
    },
    destroy: (reason: string) => {
      destroyed = true;
      connected = false;
      paused = false;
      stopBudgetTimer();
      rejectAllQueued(new LeaderUnavailableError(reason));
      for (const entry of drainInFlight()) {
        entry.reject(new LeaderUnavailableError(reason));
      }
    },
    enterHandover: () => {
      if (destroyed) {
        return;
      }
      connected = false;
      paused = false;
      handoverStartedAt ??= options.now();
      startBudgetTimer();
      // Dispatched requests are INDETERMINATE across a leader change: the old
      // leader may have applied, partially applied, or never seen them. They are
      // PARTITIONED by whether re-sending is observationally safe.
      //
      //   - RESENDABLE (a read outside any transaction): re-queued at the HEAD,
      //     in their original dispatch order, so they flush to the new leader
      //     ahead of anything enqueued after the handover began. Re-issuing a
      //     read duplicates no effect, so at-most-once buys nothing while
      //     rejecting costs the caller a spurious error.
      //   - EVERYTHING ELSE: rejected with `LeaderChangedError` and NEVER
      //     re-sent. This is settled decision D1 (at-most-once), and it is what
      //     stops a non-idempotent write from double-applying.
      const resend: PendingEntry[] = [];
      for (const entry of drainInFlight()) {
        if (entry.resendable) {
          resend.push(entry);
          continue;
        }
        entry.reject(new LeaderChangedError());
      }
      if (resend.length > 0) {
        // HEAD, not tail: these were dispatched BEFORE anything still sitting in
        // `queued`, and the queue's ordering guarantee is global FIFO.
        queued.unshift(...resend);
        notifyQueueChange();
      }
      // `queued` is deliberately RETAINED: those were never sent.
    },
    enterPause: () => {
      if (destroyed) {
        return;
      }
      // Stop dispatching so new calls accumulate, and run the same budget clock —
      // a pause that never resumes must fail like a handover that never
      // completes, rather than hanging forever.
      //
      // `inFlight` is deliberately UNTOUCHED; see `enterPause`'s contract.
      connected = false;
      paused = true;
      handoverStartedAt ??= options.now();
      startBudgetTimer();
    },
    failInFlight: (callId: string, error: Error) => {
      settleInFlight(callId)?.reject(error);
    },
    get inFlightCount() {
      return inFlight.size;
    },
    get queuedCount() {
      return queued.length;
    },
    request: async (
      method: string,
      args: readonly unknown[],
      callId: string,
      requestOptions?: { resumesPausedLeader?: boolean },
    ) =>
      boundedPromise<unknown>(
        {
          // Not open-ended: every phase is bounded by this queue's own INJECTED
          // clock (the handover budget while queued, the per-method budget while
          // in flight, and the lifetime backstop over both), which is what keeps
          // this module free of real timers and testable without fake ones.
          deadline: unboundedWait(
            "bounded by the queue's injected timers: handover budget, per-request budget, lifetime backstop",
          ),
          operation: `leader request "${method}"`,
        },
        (resolve, reject) => {
          if (destroyed) {
            reject(new LeaderUnavailableError("Leader resource client is closed"));
            return;
          }
          // `resendable` is a placeholder until `dispatchEntry` decides it; an
          // undispatched entry is never consulted for it.
          const entry: PendingEntry = {
            args,
            callId,
            cancelLifetime: null,
            cancelTimeout: null,
            method,
            reject,
            resendable: false,
            resolve,
          };
          entry.cancelLifetime = options.setTimer(() => {
            entry.cancelLifetime = null;
            // Wherever it is, it must never be dispatched (or answered) after this.
            const queuedIndex = queued.indexOf(entry);
            if (queuedIndex !== -1) {
              queued.splice(queuedIndex, 1);
              notifyQueueChange();
            }
            settleInFlight(entry.callId);
            reject(
              new LeaderUnavailableError(
                `Leader request "${method}" timed out after ${requestLifetimeMs}ms across leader changes`,
              ),
            );
          }, requestLifetimeMs);
          const settleCleanup = (): void => {
            entry.cancelLifetime?.();
            entry.cancelLifetime = null;
          };
          if (
            (connected && !flushing) ||
            (paused && requestOptions?.resumesPausedLeader === true)
          ) {
            dispatchEntry(entry);
            return settleCleanup;
          }
          // Overflow rejects OLDEST-first: the most recent user intent is the one
          // worth keeping alive, and dropping from the head bounds memory without
          // reordering what survives.
          if (queued.length >= MAX_QUEUED_REQUESTS) {
            const oldest = queued.shift();
            oldest?.reject(new LeaderUnavailableError("queue overflow"));
          }
          queued.push(entry);
          startBudgetTimer();
          notifyQueueChange();
          return settleCleanup;
        },
      ),
    resolveInFlight: (callId: string, result: unknown) => {
      settleInFlight(callId)?.resolve(result);
    },
    get sinceHandoverMs() {
      return handoverStartedAt === null ? null : options.now() - handoverStartedAt;
    },
  };
}
